// ==========================================================================
// Squarebit Store - Copyright (c) 2026 Squarebit LLC. All rights reserved.
// Proprietary and confidential. Not for redistribution.
//
// The API reference, as pages of ours rather than a framed generated site.
//
// The reference is extracted from the source when the framework is built and
// ships inside the release, so it always describes the build being offered.
// What ships is structure - names, signatures, docstrings - and never a
// function body, which is the same line the Sphinx build drew by leaving
// viewcode out.
//
// Two pages live here. The search page is the front door, because most people
// arriving at an API reference already know the name of the thing they want
// and only need its signature; they should not have to know which of the three
// packages it lives in first. The package page is for the other half, who are
// looking to see what a package can do.
// ==========================================================================

import React, { useDeferredValue, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useApiPackage, useApiRootIndex } from './api/use-api';
import {
  KIND_LABEL,
  SearchHit,
  buildSearchKeys,
  searchAll,
  searchSymbols
} from './api/search';
import { ApiModuleSummary } from './api/types';
import { RstText } from './api/rst.component';

const PACKAGES: Record<string, { label: string; blurb: string }> = {
  core: {
    label: 'core',
    blurb:
      'Scene and node utilities, deformer and weight data, and the pose ' +
      'interpolator machinery the rig templates are built on.'
  },
  mix: {
    label: 'mix',
    blurb:
      'The deformer and pose space deformer toolset, and the interfaces for ' +
      'both.'
  },
  sbgui: {
    label: 'sbgui',
    blurb: 'The Qt widget library the Squarebit tools are assembled from.'
  }
};

const PACKAGE_NAMES = Object.keys(PACKAGES);

const H2: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h2 className="font-display text-[24px] font-extrabold text-content-primary mb-[10px] tracking-[-0.4px]">
    {children}
  </h2>
);

const Lead: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-[14px] leading-[1.75] text-content-secondary mb-[20px]">
    {children}
  </p>
);

/**
 * Shown when the mirrored build is older than the reference.
 *
 * A missing file here is an ordinary state, not a fault: it means the release
 * on the site predates this section. Said in a sentence, in place, rather than
 * as an error.
 */
export const NotPublished: React.FC<{ what: string }> = ({ what }) => (
  <div className="rounded-[12px] border border-border-dark/[0.11] bg-surface-hover p-[22px]">
    <p className="text-[14px] leading-[1.7] text-content-secondary">
      The {what} is not in the published build yet. It is extracted from the
      framework when that build is made, so it appears here once a build carrying it
      has been released.
    </p>
  </div>
);

export const Loading: React.FC = () => (
  <p className="text-[13.5px] text-content-muted-alt">Loading the reference…</p>
);

const SearchField: React.FC<{
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  label: string;
  autoFocus?: boolean;
  large?: boolean;
}> = ({ value, onChange, placeholder, label, autoFocus, large }) => (
  <input
    type="search"
    value={value}
    onChange={(event) => onChange(event.target.value)}
    placeholder={placeholder}
    aria-label={label}
    // The whole point of this page is the search box, so it takes the caret.
    autoFocus={autoFocus}
    className={`w-full ${
      large ? 'h-[46px] text-[15px] px-[16px]' : 'h-[38px] text-[13px] px-[13px]'
    } rounded-[10px] bg-surface-section border border-border-dark/[0.14] font-mono text-content-primary placeholder:text-content-muted-light focus:outline-none focus:border-blue/[0.5] transition-colors`}
  />
);

// The module path already begins with the package name - `core.maya.psd` - so
// a result carries where it came from without a second label saying it again.
const HitRow: React.FC<{ hit: SearchHit }> = ({ hit }) => (
  <Link
    to={
      hit.kind === 'mod'
        ? `/sbframework/docs/api/${hit.packageName}/${hit.module}`
        : `/sbframework/docs/api/${hit.packageName}/${hit.module}#${hit.name}`
    }
    className="group block py-[9px] border-b border-border-dark/[0.07] no-underline"
  >
    <div className="flex items-baseline gap-[10px]">
      <span className="shrink-0 w-[52px] font-mono text-[10.5px] text-content-muted-light">
        {KIND_LABEL[hit.kind]}
      </span>
      <span className="font-mono text-[13.5px] text-content-primary group-hover:text-blue transition-colors">
        {hit.name}
      </span>
      <span className="font-mono text-[11.5px] text-content-muted-light truncate">
        {hit.module}
      </span>
    </div>
    {hit.summary && (
      <p className="mt-[2px] ml-[62px] text-[12.5px] leading-[1.6] text-content-muted-alt">
        <RstText text={hit.summary} />
      </p>
    )}
  </Link>
);

// ------------------------------------------------------------ search page --

/**
 * The front door: one box over all three packages.
 *
 * Every package index is fetched here, which is the whole of the searchable
 * data and still small enough to be worth having in hand the moment somebody
 * starts typing. They are cached for the session, so the package pages that
 * follow are free.
 */
const ApiSearchPage: React.FC = () => {
  const rootIndex = useApiRootIndex();
  const core = useApiPackage('core');
  const mix = useApiPackage('mix');
  const sbgui = useApiPackage('sbgui');

  const indexes = useMemo(
    () => [core.data, mix.data, sbgui.data],
    [core.data, mix.data, sbgui.data]
  );
  const keys = useMemo(
    () => indexes.map((index) => (index ? buildSearchKeys(index) : null)),
    [indexes]
  );

  const [query, setQuery] = useState('');
  const deferred = useDeferredValue(query);
  const hits = useMemo(
    () => searchAll(indexes, keys, deferred),
    [indexes, keys, deferred]
  );

  const loading = core.isLoading || mix.isLoading || sbgui.isLoading;
  const anything = indexes.some(Boolean);
  const symbolCount = indexes.reduce(
    (total, index) => total + (index?.symbols.length ?? 0),
    0
  );

  return (
    <article className="font-body text-content-primary">
      <H2>API reference</H2>
      <Lead>
        Extracted from the framework build you have and shipped inside it, so it
        describes the build you are running. Search every package at once, or browse
        one of them below.
      </Lead>

      {loading && <Loading />}
      {!loading && !anything && <NotPublished what="API reference" />}

      {anything && (
        <>
          <SearchField
            large
            autoFocus
            value={query}
            onChange={setQuery}
            placeholder={`Search ${symbolCount} symbols`}
            label="Search the API reference"
          />

          {deferred.trim() ? (
            <div className="mt-[18px]">
              {hits.length === 0 ? (
                <p className="text-[13.5px] text-content-muted-alt">
                  Nothing matches “{deferred.trim()}”.
                </p>
              ) : (
                hits.map((hit) => (
                  <HitRow
                    key={`${hit.packageName}.${hit.module}.${hit.name}.${hit.kind}`}
                    hit={hit}
                  />
                ))
              )}
            </div>
          ) : (
            <div className="mt-[24px] grid gap-[12px] sm:grid-cols-3">
              {PACKAGE_NAMES.map((name) => {
                const counts = rootIndex.data?.packages.find(
                  (item) => item.package === name
                );
                return (
                  <Link
                    key={name}
                    to={`/sbframework/docs/api/${name}`}
                    className="block rounded-[12px] border border-border-dark/[0.11] bg-surface-card p-[18px] no-underline hover:border-blue/[0.35] transition-colors"
                  >
                    <span className="font-mono text-[15px] font-semibold text-content-primary">
                      {PACKAGES[name].label}
                    </span>
                    <p className="mt-[8px] text-[13px] leading-[1.65] text-content-secondary">
                      {PACKAGES[name].blurb}
                    </p>
                    {counts && (
                      <p className="mt-[10px] text-[11.5px] text-content-muted-light">
                        {counts.modules} modules · {counts.classes} classes ·{' '}
                        {counts.functions} functions
                      </p>
                    )}
                  </Link>
                );
              })}
            </div>
          )}
        </>
      )}
    </article>
  );
};

// ----------------------------------------------------------- package page --

/** `core.maya.psd` belongs under `core.maya`. */
const namespaceOf = (module: string): string => {
  const parts = module.split('.');
  return parts.length > 1 ? parts.slice(0, -1).join('.') : module;
};

const groupModules = (
  modules: ApiModuleSummary[]
): { namespace: string; modules: ApiModuleSummary[] }[] => {
  const groups = new Map<string, ApiModuleSummary[]>();
  modules.forEach((entry) => {
    const key = namespaceOf(entry.module);
    const bucket = groups.get(key);
    if (bucket) {
      bucket.push(entry);
    } else {
      groups.set(key, [entry]);
    }
  });
  return Array.from(groups, ([namespace, entries]) => ({
    namespace,
    modules: entries
  })).sort((a, b) => a.namespace.localeCompare(b.namespace));
};

const ModuleRow: React.FC<{ entry: ApiModuleSummary; to: string }> = ({
  entry,
  to
}) => (
  <Link
    to={to}
    className="group block py-[9px] border-b border-border-dark/[0.07] no-underline"
  >
    <div className="flex items-baseline justify-between gap-[14px]">
      <span className="font-mono text-[13px] text-content-primary group-hover:text-blue transition-colors">
        {entry.module}
      </span>
      <span className="shrink-0 text-[11.5px] text-content-muted-light">
        {[
          entry.classes > 0 &&
            `${entry.classes} class${entry.classes === 1 ? '' : 'es'}`,
          entry.functions > 0 && `${entry.functions} fn`
        ]
          .filter(Boolean)
          .join(' · ')}
      </span>
    </div>
    {entry.summary && (
      <p className="mt-[2px] text-[12.5px] leading-[1.6] text-content-muted-alt">
        <RstText text={entry.summary} />
      </p>
    )}
  </Link>
);

const ApiPackagePage: React.FC<{ packageName: string }> = ({ packageName }) => {
  const entry = PACKAGES[packageName];
  const index = useApiPackage(packageName);

  const [query, setQuery] = useState('');
  const deferred = useDeferredValue(query);

  const keys = useMemo(
    () => (index.data ? buildSearchKeys(index.data) : null),
    [index.data]
  );
  const hits = useMemo(
    () => (index.data && keys ? searchSymbols(index.data, keys, deferred) : []),
    [index.data, keys, deferred]
  );
  const groups = useMemo(
    () => (index.data ? groupModules(index.data.modules) : []),
    [index.data]
  );

  return (
    <article className="font-body text-content-primary">
      <Link
        to="/sbframework/docs/api"
        className="text-[12.5px] text-content-muted-alt hover:text-blue no-underline transition-colors"
      >
        ← API reference
      </Link>

      <div className="mt-[8px]">
        <H2>{entry.label} API</H2>
      </div>
      <Lead>
        {entry.blurb} Generated from the build you have, and shipped with it.
      </Lead>

      {index.isLoading && <Loading />}
      {!index.isLoading && !index.data && (
        <NotPublished what={`${entry.label} reference`} />
      )}

      {index.data && (
        <>
          <SearchField
            value={query}
            onChange={setQuery}
            placeholder={`Search ${index.data.symbols.length} symbols in ${entry.label}`}
            label={`Search the ${entry.label} reference`}
          />

          {deferred.trim() ? (
            <div className="mt-[18px]">
              {hits.length === 0 ? (
                <p className="text-[13.5px] text-content-muted-alt">
                  Nothing in {entry.label} matches “{deferred.trim()}”.
                </p>
              ) : (
                hits.map((hit) => (
                  <HitRow key={`${hit.module}.${hit.name}.${hit.kind}`} hit={hit} />
                ))
              )}
            </div>
          ) : (
            <div className="mt-[22px]">
              {groups.map((group) => (
                <section key={group.namespace} className="mb-[30px]">
                  <h3 className="font-mono text-[12px] uppercase tracking-[0.6px] text-content-muted-light mb-[6px]">
                    {group.namespace}
                  </h3>
                  {group.modules.map((module) => (
                    <ModuleRow
                      key={module.module}
                      entry={module}
                      to={`/sbframework/docs/api/${packageName}/${module.module}`}
                    />
                  ))}
                </section>
              ))}
            </div>
          )}
        </>
      )}
    </article>
  );
};

const SbFrameworkApiDoc: React.FC = () => {
  const { package: packageName } = useParams<{ package: string }>();
  // An unknown package name falls back to the search page rather than a dead
  // end, which is also what a hand-typed URL usually wants.
  return packageName && PACKAGES[packageName] ? (
    <ApiPackagePage packageName={packageName} />
  ) : (
    <ApiSearchPage />
  );
};

export default SbFrameworkApiDoc;
